import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ClientProjectPicker } from "./ClientProjectPicker";
import { AssumptionsPanel } from "./AssumptionsPanel";
import { PromptDialog } from "@/components/PromptDialog";
import {
  Briefcase, Target, Loader2, Sparkles, Copy, RefreshCw, CheckCircle2, FileDown,
  Download, Trash2, FolderPlus, Save, Brain, AlertTriangle, FileText, BookmarkPlus,
} from "lucide-react";

const SEL = "w-full bg-navy border border-white/20 text-white p-2.5 text-sm focus:border-gold focus:outline-none";
const LBL = "block text-xs uppercase tracking-wide text-gray-500 mb-1.5";

const PLAN_OBJECTIVES = [
  "Launch a Business", "Secure Startup Funding", "Obtain a Business Loan", "Attract Investors",
  "Validate the Business Idea", "Reach Profitability", "Increase Revenue", "Expand the Business",
  "Enter a New Market", "Launch a New Product or Service", "Build a Customer Base", "Create a Marketing Strategy",
  "Develop an Operations Plan", "Hire & Build a Team", "Improve Cash Flow", "Purchase Equipment / Assets",
  "Prepare for Grants", "Nonprofit / Community Impact", "Create a Growth Strategy", "Education / Classroom Exercise",
  "Other (Custom)",
];

const PLAN_PURPOSES = ["Startup Planning", "Bank / SBA Loan", "Investor", "Grant", "Internal Growth", "Classroom / Training"];

// objective → recommended section ids (union across selected). Recommendations, not locks.
const OBJECTIVE_PRESETS = {
  "Launch a Business": ["exec_summary", "company", "problem_solution", "products", "market", "target_customer", "competitive", "marketing", "operations", "management", "startup_costs", "breakeven", "financials", "funding", "milestones", "risks"],
  "Secure Startup Funding": ["exec_summary", "company", "market", "competitive", "marketing", "operations", "startup_costs", "financials", "breakeven", "cashflow", "funding", "risks"],
  "Obtain a Business Loan": ["exec_summary", "company", "market", "competitive", "operations", "startup_costs", "financials", "breakeven", "cashflow", "funding", "risks"],
  "Attract Investors": ["exec_summary", "company", "problem_solution", "market", "target_customer", "competitive", "products", "marketing", "financials", "milestones", "funding", "swot"],
  "Validate the Business Idea": ["problem_solution", "target_customer", "market", "competitive", "products", "startup_costs", "breakeven", "risks", "swot"],
  "Reach Profitability": ["exec_summary", "products", "marketing", "operations", "breakeven", "financials", "cashflow"],
  "Increase Revenue": ["exec_summary", "market", "target_customer", "competitive", "marketing", "products", "financials", "milestones"],
  "Expand the Business": ["exec_summary", "company", "market", "competitive", "operations", "management", "financials", "milestones", "funding", "risks"],
  "Enter a New Market": ["exec_summary", "market", "target_customer", "competitive", "marketing", "operations", "financials", "risks"],
  "Launch a New Product or Service": ["exec_summary", "products", "market", "target_customer", "competitive", "marketing", "financials", "milestones"],
  "Build a Customer Base": ["exec_summary", "target_customer", "market", "competitive", "marketing", "milestones"],
  "Create a Marketing Strategy": ["exec_summary", "target_customer", "market", "competitive", "marketing", "milestones"],
  "Develop an Operations Plan": ["exec_summary", "operations", "management", "startup_costs", "milestones"],
  "Hire & Build a Team": ["exec_summary", "company", "management", "operations", "financials", "milestones"],
  "Improve Cash Flow": ["exec_summary", "operations", "breakeven", "financials", "cashflow"],
  "Purchase Equipment / Assets": ["exec_summary", "operations", "startup_costs", "financials", "funding"],
  "Prepare for Grants": ["exec_summary", "company", "mission_vision", "problem_solution", "market", "operations", "financials", "funding", "appendix"],
  "Nonprofit / Community Impact": ["exec_summary", "company", "mission_vision", "problem_solution", "market", "operations", "financials", "funding", "milestones", "appendix"],
  "Create a Growth Strategy": ["exec_summary", "company", "market", "competitive", "marketing", "operations", "financials", "milestones"],
  "Education / Classroom Exercise": ["exec_summary", "problem_solution", "target_customer", "competitive", "marketing", "operations", "startup_costs", "financials", "breakeven", "funding", "milestones"],
};

export function BusinessPlanBuilder({ config, clients, projects, clientId, setClientId, projectId, setProjectId, reloadClients, reloadProjects }) {
  const blocks = config.bizplan_blocks || [];
  const fullBlocks = useMemo(() => blocks.filter((b) => b.in_full), [blocks]);

  const [f, setF] = useState({ plan_name: "", brand_color: "#C9A227", plan_purpose: "", context: "", industry: "", goal: [], audience: "", tone: "Professional", model: "auto", target_city: "", target_state: "", industry_custom: "", audience_custom: "", goal_custom: "", student_mode: false });
  const CUSTOM = "Other (Custom)";
  const isCustom = (v) => typeof v === "string" && (v === CUSTOM || /\(custom\)/i.test(v));
  const [fin, setFin] = useState(null); // authoritative financials from the Assumptions Panel
  const onAsmChange = useCallback((_a, financials) => setFin(financials), []);
  const toggleGoal = (g) => setF((p) => ({ ...p, goal: p.goal.includes(g) ? p.goal.filter((x) => x !== g) : [...p.goal, g] }));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [mode, setMode] = useState("full");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [genMeta, setGenMeta] = useState(null);
  const [genProjectId, setGenProjectId] = useState(null);
  const [blockBusy, setBlockBusy] = useState({});
  const [attachProject, setAttachProject] = useState("");
  const [attachName, setAttachName] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [onePagerBusy, setOnePagerBusy] = useState(false);
  const [tplBusy, setTplBusy] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [tplId, setTplId] = useState("");
  const [bi, setBi] = useState(null);

  useEffect(() => {
    if (!clientId && !projectId) { setBi(null); return; }
    api.get("/ai/studio/business-intelligence", { params: { client_id: clientId || undefined, project_id: projectId || undefined, generator: "proposal" } })
      .then((r) => {
        setBi(r.data);
        if (r.data?.loaded) {
          const fld = r.data.fields || {};
          setF((p) => ({
            ...p,
            context: p.context || fld.context?.value || "",
            industry: p.industry || fld.industry?.value || "",
            audience: p.audience || fld.audience?.value || "",
          }));
        }
      }).catch(() => setBi(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, projectId]);

  const toggleBlock = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(blocks.map((b) => b.id)));
  const clearAll = () => setSelected(new Set());

  const recommended = useMemo(() => {
    const ids = new Set();
    (f.goal || []).forEach((g) => (OBJECTIVE_PRESETS[g] || []).forEach((id) => ids.add(id)));
    return blocks.filter((b) => ids.has(b.id)).map((b) => b.id); // ordered by block order
  }, [f.goal, blocks]);
  const [recApplied, setRecApplied] = useState(false);
  const applyRecommended = () => { setMode("custom"); setSelected(new Set(recommended)); setRecApplied(true); };
  const goCustom = () => { setMode("custom"); if (selected.size === 0 && recommended.length) { setSelected(new Set(recommended)); setRecApplied(true); } };
  const chosenCount = mode === "full" ? fullBlocks.length : selected.size;
  const projectName = (id) => (projects.find((p) => p.id === id) || {}).org_name || "";
  const planTitle = () => f.plan_name.trim() || projectName(genProjectId || projectId) || "Business Plan";

  const generate = async () => {
    if (!f.context.trim()) { toast.error("Describe the business first"); return; }
    const chosen = mode === "full" ? fullBlocks.map((b) => b.id) : [...selected];
    if (mode === "custom" && chosen.length === 0) { toast.error("Select at least one section"); return; }
    setBusy(true); setResults(null); setGenProjectId(null);
    try {
      const advanced = {};
      ["target_city", "target_state"].forEach((k) => { if (f[k]) advanced[k] = f[k]; });
      const industryVal = isCustom(f.industry) ? (f.industry_custom.trim() || f.industry) : f.industry;
      const audienceVal = isCustom(f.audience) ? (f.audience_custom.trim() || f.audience) : f.audience;
      const goalList = (f.goal || []).map((g) => isCustom(g) ? (f.goal_custom.trim() || g) : g).filter(Boolean);
      const { data } = await api.post("/ai/studio/generate-plan-batch", {
        mode, blocks: chosen, context: f.context, industry: industryVal || null, goal: goalList.length ? goalList.join(", ") : null,
        audience: audienceVal || null, tone: f.tone, model: f.model, advanced, student_mode: f.student_mode,
        plan_purpose: f.plan_purpose || null,
        client_id: clientId || null, project_id: projectId || null,
      });
      setResults(data.results); setGenProjectId(projectId || null);
      setGenMeta({ cost: data.total_cost_estimated, tokens: data.total_tokens });
      const failed = data.count - data.generated;
      toast.success(`Generated ${data.generated}/${data.count} section${data.count === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const setBB = (id, v) => setBlockBusy((p) => ({ ...p, [id]: v }));
  const updateResult = (id, patch) => setResults((rs) => rs.map((r) => (r.block_id === id ? { ...r, ...patch } : r)));

  const regen = async (r) => {
    setBB(r.block_id, "regen");
    try {
      const { data } = await api.post("/ai/studio/refine", { asset_id: r.asset_id, action: "regenerate", model: f.model });
      updateResult(r.block_id, { result: data.result, version: data.version, review_status: "edited" });
      toast.success(`Regenerated → v${data.version}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBB(r.block_id, null); }
  };
  const approve = async (r) => {
    setBB(r.block_id, "approve");
    try {
      await api.post(`/ai/studio/assets/${r.asset_id}/approve`);
      updateResult(r.block_id, { review_status: "approved" });
      toast.success(`${r.label} approved`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBB(r.block_id, null); }
  };
  const copyOne = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); } };
  const exportOne = async (r) => {
    try {
      const res = await api.post("/ai/studio/export/docx", { asset_id: r.asset_id }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${r.label.replace(/[^a-z0-9]+/gi, "-")}.docx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const okResults = (results || []).filter((r) => r.ok);
  const okAssetIds = okResults.filter((r) => r.asset_id).map((r) => r.asset_id);
  const combinedText = () => `# ${planTitle()} — Business Plan\n\n` + okResults.map((r) => `## ${r.label}\n\n${r.result}`).join("\n\n---\n\n");

  const attachExisting = async () => {
    if (!attachProject) { toast.error("Pick a project"); return; }
    setAttaching(true);
    try { const { data } = await api.post("/ai/studio/batch/attach", { asset_ids: okAssetIds, project_id: attachProject });
      setGenProjectId(data.project_id); toast.success(`Saved ${data.attached} section(s) to project`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setAttaching(false); }
  };
  const attachNew = async () => {
    if (!attachName.trim()) { toast.error("Enter a project name"); return; }
    setAttaching(true);
    try { const { data } = await api.post("/ai/studio/batch/attach", { asset_ids: okAssetIds, new_project_name: attachName, client_id: clientId || null });
      setGenProjectId(data.project_id); toast.success(`Created “${attachName}” & saved ${data.attached} section(s)`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setAttaching(false); }
  };
  const downloadPdf = async () => {
    if (okResults.length === 0) { toast.error("Generate the plan first"); return; }
    setPdfBusy(true);
    try {
      const res = await api.post("/ai/studio/plan/export-pdf", {
        business_name: planTitle(), project_id: genProjectId || projectId || null, brand_color: f.brand_color,
        financials: fin,
        sections: okResults.map((r) => ({ block_id: r.block_id, label: r.label, result: r.result })),
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${planTitle().replace(/[^a-z0-9]+/gi, "-")}-business-plan.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Business plan PDF downloaded");
    } catch (e) { toast.error(formatApiError(e)); } finally { setPdfBusy(false); }
  };
  const downloadOnePager = async () => {
    if (okResults.length === 0) { toast.error("Generate the plan first"); return; }
    setOnePagerBusy(true);
    try {
      const res = await api.post("/ai/studio/plan/one-pager-pdf", {
        business_name: planTitle(), project_id: genProjectId || projectId || null, brand_color: f.brand_color,
        plan_purpose: f.plan_purpose || null,
        financials: fin,
        sections: okResults.map((r) => ({ block_id: r.block_id, label: r.label, result: r.result })),
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${planTitle().replace(/[^a-z0-9]+/gi, "-")}-loan-summary.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Lender one-pager downloaded");
    } catch (e) { toast.error(formatApiError(e)); } finally { setOnePagerBusy(false); }
  };
  const [tplPrompt, setTplPrompt] = useState(false);
  const saveAsTemplate = () => {
    if (okResults.length === 0) { toast.error("Generate the plan first"); return; }
    setTplPrompt(true);
  };
  const doSaveAsTemplate = async (title) => {
    setTplPrompt(false);
    setTplBusy(true);
    try {
      const category = isCustom(f.industry) ? (f.industry_custom.trim() || "General") : (f.industry || "General");
      await api.post("/ai/studio/plan-templates", { title: title.trim(), category,
        sections: okResults.map((r) => ({ block_id: r.block_id, label: r.label, result: r.result })) });
      toast.success("Saved as a reusable plan template");
      loadTemplates();
    } catch (e) { toast.error(formatApiError(e)); } finally { setTplBusy(false); }
  };
  const loadTemplates = async () => {
    try { const { data } = await api.get("/ai/studio/plan-templates"); setTemplates(data.templates || []); } catch { setTemplates([]); }
  };
  const applyTemplate = async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/ai/studio/plan-templates/${id}`);
      const secs = data.sections || [];
      setResults(secs.map((s) => ({ block_id: s.block_id, label: s.label, emoji: (blocks.find((b) => b.id === s.block_id) || {}).emoji || "📄", result: s.result, ok: true, templated: true, review_status: "template" })));
      setGenProjectId(null); setGenMeta(null);
      if (data.title) set("plan_name", data.title);
      toast.success(`Loaded template “${data.title}” — edit, then export or save`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadTemplates(); /* eslint-disable-next-line */ }, []);
  const downloadAll = () => {
    const blob = new Blob([combinedText()], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "business-plan.md"; a.click(); URL.revokeObjectURL(url);
  };
  const copyAll = async () => { try { await navigator.clipboard.writeText(combinedText()); toast.success("Full plan copied"); } catch { toast.error("Copy failed"); } };
  const discard = () => { setResults(null); setGenProjectId(null); setGenMeta(null); };

  const statusChip = (s) => s === "approved" ? "border-emerald-500/40 text-emerald-300"
    : s === "edited" ? "border-amber-400/40 text-amber-300" : "border-white/15 text-gray-400";

  return (
    <div className="space-y-5" data-testid="business-plan-builder">
      {bi?.loaded && (
        <div className="bg-navy-800 border border-gold/20 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="bp-bi">
          <div className="flex items-center gap-2 text-white font-medium"><Brain size={16} className="text-gold" /> Loaded from Business Intelligence
            {bi.company?.org_name && <span className="text-gray-400 text-sm">· {bi.company.org_name}</span>}</div>
          <div className="flex items-center gap-2 text-xs text-gray-500"><span>Knowledge</span>
            <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gold" style={{ width: `${bi.knowledge?.score || 0}%` }} /></div>
            <span className="text-gold font-heading">{bi.knowledge?.score || 0}%</span></div>
        </div>
      )}

      <div className="bg-navy-800 border border-white/10 p-5 space-y-4" data-testid="bp-mission">
        <div className="flex items-center gap-2 text-white font-medium"><Briefcase size={18} className="text-gold" /> Business Plan Builder
          <span className="text-gray-500 text-sm font-normal">— one brief, a complete bank-ready plan</span></div>

        <ClientProjectPicker clients={clients} projects={projects} clientId={clientId} setClientId={setClientId}
          projectId={projectId} setProjectId={setProjectId} reloadClients={reloadClients} reloadProjects={reloadProjects} />

        <div className="grid md:grid-cols-4 gap-4">
          <div className="md:col-span-2"><label className={LBL}>Business name <span className="text-gray-500 font-normal normal-case">· for the plan title & PDF</span></label>
            <input className={SEL} value={f.plan_name} onChange={(e) => set("plan_name", e.target.value)} data-testid="bp-plan-name" placeholder="e.g. Riverside Coffee Co." /></div>
          <div><label className={LBL}>Brand color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={f.brand_color} onChange={(e) => set("brand_color", e.target.value)} data-testid="bp-brand-color" className="h-9 w-10 bg-navy border border-white/20 cursor-pointer" />
              <input className={SEL} value={f.brand_color} onChange={(e) => set("brand_color", e.target.value)} data-testid="bp-brand-color-hex" /></div></div>
          <div><label className={LBL}>AI model</label>
            <select className={SEL} value={f.model} onChange={(e) => set("model", e.target.value)} data-testid="bp-model">
              {config.models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select></div>
        </div>

        {templates && templates.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="bp-template-loader">
            <FileText size={13} className="text-gold" /> Start from a saved template
            <select className={`${SEL} max-w-xs`} value={tplId} onChange={(e) => { setTplId(e.target.value); applyTemplate(e.target.value); }} data-testid="bp-template-select">
              <option value="">— none —</option>
              {Object.entries(templates.reduce((acc, t) => { const c = t.category || "General"; (acc[c] = acc[c] || []).push(t); return acc; }, {})).map(([cat, list]) => (
                <optgroup key={cat} label={cat}>
                  {list.map((t) => <option key={t.id} value={t.id} label={`${t.title} (${t.section_count})`} />)}
                </optgroup>
              ))}
            </select>
          </div>
        )}

        <div><label className={LBL}>Business description / brief</label>
          <p className="text-[11px] text-gray-400 -mt-1 mb-1.5">Include: what you sell • target customer • location • owner experience • employees • startup needs • funding needed</p>
          <textarea rows={4} className={SEL} value={f.context} onChange={(e) => set("context", e.target.value)} data-testid="bp-context"
            placeholder="e.g. A drive-thru specialty coffee shop in Montgomery, AL. Sells espresso drinks, cold brew and pastries to commuters and students. Owner has 5 yrs barista experience, seeking a startup loan. Two employees at launch, one location." /></div>

        <div><label className={LBL}>Plan purpose <span className="text-gray-500 font-normal normal-case">· who is this plan for?</span></label>
          <select className={SEL} value={f.plan_purpose} onChange={(e) => set("plan_purpose", e.target.value)} data-testid="bp-plan-purpose">
            <option value="">Select…</option>{PLAN_PURPOSES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select></div>

        <div className="grid md:grid-cols-4 gap-4">
          <div><label className={LBL}>Industry</label>
            <select className={SEL} value={f.industry} onChange={(e) => set("industry", e.target.value)} data-testid="bp-industry">
              <option value="">Select…</option>{config.industries.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {isCustom(f.industry) && <input className={`${SEL} mt-1.5`} value={f.industry_custom} onChange={(e) => set("industry_custom", e.target.value)} data-testid="bp-industry-custom" placeholder="Describe the industry…" />}
          </div>
          <div className="md:col-span-2"><label className={LBL}>Business plan objective <span className="text-gray-500 font-normal normal-case">· pick one or more</span></label>
            <div className="flex flex-wrap gap-1.5 p-2 border border-white/15 rounded bg-navy max-h-28 overflow-y-auto" data-testid="bp-objectives">
              {PLAN_OBJECTIVES.map((o) => {
                const on = f.goal.includes(o);
                return <button type="button" key={o} onClick={() => toggleGoal(o)} data-testid={`bp-objective-${o}`}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${on ? "bg-gold text-navy border-gold font-medium" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>{o}</button>;
              })}
            </div>
            {f.goal.some(isCustom) && <input className={`${SEL} mt-1.5`} value={f.goal_custom} onChange={(e) => set("goal_custom", e.target.value)} data-testid="bp-goal-custom" placeholder="Describe your custom objective…" />}
          </div>
          <div><label className={LBL}>Audience</label>
            <select className={SEL} value={f.audience} onChange={(e) => set("audience", e.target.value)} data-testid="bp-audience">
              <option value="">Select…</option>{config.audiences.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {isCustom(f.audience) && <input className={`${SEL} mt-1.5`} value={f.audience_custom} onChange={(e) => set("audience_custom", e.target.value)} data-testid="bp-audience-custom" placeholder="Describe the audience…" />}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div><label className={LBL}>Target city</label><input className={SEL} value={f.target_city} onChange={(e) => set("target_city", e.target.value)} data-testid="bp-city" placeholder="Montgomery" /></div>
          <div><label className={LBL}>Target state</label><input className={SEL} value={f.target_state} onChange={(e) => set("target_state", e.target.value)} data-testid="bp-state" placeholder="AL" /></div>
          <div><label className={LBL}>Tone</label>
            <select className={SEL} value={f.tone} onChange={(e) => set("tone", e.target.value)} data-testid="bp-tone">
              {config.tones.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></div>
        </div>

        <label className="flex items-center gap-2.5 p-3 border border-white/10 bg-navy cursor-pointer hover:border-white/25" data-testid="bp-student-mode-wrap">
          <input type="checkbox" className="accent-gold w-4 h-4" checked={f.student_mode} onChange={(e) => set("student_mode", e.target.checked)} data-testid="bp-student-mode" />
          <span className="text-sm text-gray-200 font-medium">🎓 Student / teaching mode</span>
          <span className="text-xs text-gray-500">— adds a plain-language “Why this matters” note after each key number, great for classrooms</span>
        </label>

        {recommended.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap bg-gold/5 border border-gold/25 px-4 py-3" data-testid="bp-recommend-banner">
            <Sparkles size={15} className="text-gold shrink-0" />
            <span className="text-sm text-gray-200">AIPOS recommends <span className="text-gold font-medium">{recommended.length} sections</span> based on your objectives.</span>
            <button onClick={applyRecommended} data-testid="bp-apply-recommended" className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gold text-navy hover:bg-gold-light">Use recommended</button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <button onClick={() => setMode("full")} data-testid="bp-mode-full"
            className={`flex items-start gap-3 p-4 border text-left transition-colors ${mode === "full" ? "border-gold/60 bg-gold/10" : "border-white/10 bg-navy hover:border-white/25"}`}>
            <Briefcase size={18} className="text-gold mt-0.5" />
            <div><div className="text-white text-sm font-medium">Full Business Plan</div>
              <div className="text-gray-500 text-xs mt-0.5">One click → {fullBlocks.length} sections incl. startup costs, break-even & funding request.</div></div>
          </button>
          <button onClick={goCustom} data-testid="bp-mode-custom"
            className={`flex items-start gap-3 p-4 border text-left transition-colors ${mode === "custom" ? "border-gold/60 bg-gold/10" : "border-white/10 bg-navy hover:border-white/25"}`}>
            <Target size={18} className="text-gold mt-0.5" />
            <div><div className="text-white text-sm font-medium">Custom Sections</div>
              <div className="text-gray-500 text-xs mt-0.5">{recommended.length ? `${recommended.length} recommended for your objectives — ` : ""}pick exactly the sections you need.</div></div>
          </button>
        </div>

        {mode === "custom" && (
          <div className="border border-white/10 p-4" data-testid="bp-custom-picker">
            {recApplied && recommended.length > 0 && (
              <p className="text-[11px] text-gold mb-3" data-testid="bp-recommend-note">✨ Recommended sections selected based on your Business Plan Objectives. You can add or remove sections before generating.</p>
            )}
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wide text-gray-500">Choose sections ({selected.size})</span>
              <div className="flex gap-2">
                {recommended.length > 0 && <><button onClick={applyRecommended} data-testid="bp-picker-apply-rec" className="text-xs text-gold hover:text-gold-light">Recommended</button><span className="text-gray-700">·</span></>}
                <button onClick={selectAll} data-testid="bp-select-all" className="text-xs text-gold hover:text-gold-light">Select all</button>
                <span className="text-gray-700">·</span><button onClick={clearAll} data-testid="bp-clear" className="text-xs text-gray-400 hover:text-white">Clear</button></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {blocks.map((b) => (
                <label key={b.id} data-testid={`bp-block-${b.id}`}
                  className={`flex items-center gap-2 px-3 py-2 border text-sm cursor-pointer transition-colors ${selected.has(b.id) ? "border-gold/50 bg-gold/10 text-white" : "border-white/10 text-gray-300 hover:border-white/25"}`}>
                  <input type="checkbox" className="accent-gold" checked={selected.has(b.id)} onChange={() => toggleBlock(b.id)} />
                  <span>{b.emoji} {b.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <button onClick={generate} disabled={busy} data-testid="bp-generate-btn"
          className="w-full flex items-center justify-center gap-2 bg-gold text-navy py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={17} /> : <Sparkles size={17} />}
          {busy ? "Writing the business plan…" : `Generate ${mode === "full" ? "Full Business Plan" : `${chosenCount} section${chosenCount === 1 ? "" : "s"}`}`}
        </button>
      </div>

      {busy && <div className="text-center text-gray-500 text-sm" data-testid="bp-generating-note">Writing {chosenCount} section{chosenCount === 1 ? "" : "s"} from one shared brief — a full plan can take a moment.</div>}

      <AssumptionsPanel studentMode={f.student_mode} onChange={onAsmChange} />

      {results && (
        <div className="space-y-4" data-testid="bp-results">
          {genMeta && (genMeta.cost != null || genMeta.tokens != null) && (
            <div className="flex items-center gap-2 text-xs" data-testid="bp-gen-cost">
              <span className="px-2.5 py-1 border border-gold/30 text-gold rounded-full">Generation cost: ${Number(genMeta.cost || 0).toFixed(4)} <span className="text-gray-500">est</span></span>
              {genMeta.tokens != null && <span className="px-2.5 py-1 border border-white/15 text-gray-300 rounded-full">{genMeta.tokens.toLocaleString()} tokens</span>}
            </div>
          )}

          {okResults.length > 0 && (
            <div className="bg-navy-800 border border-gold/20 p-4 space-y-3" data-testid="bp-deliver-box">
              <div className="flex items-center gap-2 text-white text-sm font-medium"><FileText size={15} className="text-gold" /> Deliver the plan</div>
              <div className="flex flex-wrap gap-2">
                <button onClick={downloadPdf} disabled={pdfBusy} data-testid="bp-download-pdf"
                  className="inline-flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light disabled:opacity-50">
                  {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Download full plan (PDF)
                </button>
                <button onClick={downloadOnePager} disabled={onePagerBusy} data-testid="bp-download-onepager"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50">
                  {onePagerBusy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} {/bank|sba/i.test(f.plan_purpose) ? "Bank/SBA one-pager" : /investor/i.test(f.plan_purpose) ? "Investor one-pager" : /grant/i.test(f.plan_purpose) ? "Grant one-pager" : "Executive one-pager"}
                </button>
                <button onClick={saveAsTemplate} disabled={tplBusy} data-testid="bp-save-template"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-white/15 text-gray-200 hover:border-gold/50 disabled:opacity-50">
                  {tplBusy ? <Loader2 size={13} className="animate-spin" /> : <BookmarkPlus size={13} />} Save as template
                </button>
                <button onClick={downloadAll} data-testid="bp-download-all" className="flex items-center gap-1.5 px-3 py-2 text-xs border border-white/15 text-gray-200 hover:border-gold/50"><Download size={13} /> Markdown</button>
                <button onClick={copyAll} data-testid="bp-copy-all" className="flex items-center gap-1.5 px-3 py-2 text-xs border border-white/15 text-gray-200 hover:border-gold/50"><Copy size={13} /> Copy all</button>
                <button onClick={discard} data-testid="bp-discard" className="flex items-center gap-1.5 px-3 py-2 text-xs border border-red-500/30 text-red-300 hover:bg-red-500/10 ml-auto"><Trash2 size={13} /> Discard</button>
              </div>
              <div className="text-[11px] text-gray-500" data-testid="bp-financials-source-note">
                Financials in the PDF & one-pager use your <span className="text-gold">Assumptions &amp; Ratios</span> above — edit them to keep every figure consistent.
              </div>
            </div>
          )}

          {genProjectId ? (
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/25 px-4 py-3 text-emerald-300 text-sm" data-testid="bp-autosaved">
              <CheckCircle2 size={16} /> Saved to project. All generated sections are attached.
            </div>
          ) : okAssetIds.length > 0 && (
            <div className="bg-navy-800 border border-gold/20 p-4 space-y-3" data-testid="bp-save-bar">
              <div className="text-white text-sm font-medium">Save this plan to a project</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex gap-2">
                  <select className={SEL} value={attachProject} onChange={(e) => setAttachProject(e.target.value)} data-testid="bp-attach-project">
                    <option value="">Save to existing project…</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.org_name}</option>)}
                  </select>
                  <button onClick={attachExisting} disabled={attaching} data-testid="bp-attach-existing-btn"
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50"><Save size={13} /> Save</button>
                </div>
                <div className="flex gap-2">
                  <input className={SEL} value={attachName} onChange={(e) => setAttachName(e.target.value)} placeholder="…or create a new project" data-testid="bp-new-project-name" />
                  <button onClick={attachNew} disabled={attaching} data-testid="bp-attach-new-btn"
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50"><FolderPlus size={13} /> Create</button>
                </div>
              </div>
            </div>
          )}

          {results.map((r) => (
            <div key={r.block_id} className={`bg-navy-800 border p-5 space-y-3 ${r.ok ? "border-gold/25" : "border-red-500/30"}`} data-testid={`bp-result-${r.block_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-white font-medium">{r.emoji} {r.label}</div>
                {r.ok ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
                    <span className="px-2 py-0.5 border border-white/15 rounded-full">v{r.version}</span>
                    <span className="px-2 py-0.5 border border-white/15 rounded-full">{r.provider} · {r.model?.split("/").pop()}</span>
                    <span className={`px-2 py-0.5 border rounded-full capitalize ${statusChip(r.review_status)}`}>{r.review_status}</span>
                  </div>
                ) : <span className="flex items-center gap-1 text-red-300 text-xs"><AlertTriangle size={13} /> {r.error || "Failed"}</span>}
              </div>
              {r.ok && (
                <>
                  <textarea value={r.result} onChange={(e) => updateResult(r.block_id, { result: e.target.value })} rows={9}
                    data-testid={`bp-result-text-${r.block_id}`} className="w-full bg-navy border border-white/15 text-gray-100 p-3 text-sm leading-relaxed focus:border-gold focus:outline-none font-mono" />
                  <div className="flex flex-wrap gap-2">
                    {r.asset_id && <button onClick={() => regen(r)} disabled={!!blockBusy[r.block_id]} data-testid={`bp-regen-${r.block_id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-200 hover:border-gold/50 disabled:opacity-50">{blockBusy[r.block_id] === "regen" ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Regenerate</button>}
                    {r.asset_id && <button onClick={() => approve(r)} disabled={!!blockBusy[r.block_id] || r.review_status === "approved"} data-testid={`bp-approve-${r.block_id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50">{blockBusy[r.block_id] === "approve" ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle2 size={13} />} {r.review_status === "approved" ? "Approved" : "Approve"}</button>}
                    <button onClick={() => copyOne(r.result)} data-testid={`bp-copy-${r.block_id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-200 hover:border-gold/50"><Copy size={13} /> Copy</button>
                    {r.asset_id && <button onClick={() => exportOne(r)} data-testid={`bp-export-${r.block_id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-200 hover:border-gold/50"><FileDown size={13} /> Word</button>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <PromptDialog open={tplPrompt} title="Save as plan template" label="Template name"
        placeholder="e.g. Coffee Shop Starter" defaultValue={planTitle()} confirmText="Save template"
        onSubmit={doSaveAsTemplate} onClose={() => setTplPrompt(false)} />
    </div>
  );
}
